// this is the logged-in dashboard - shows your plan, lets you upgrade, sign out, etc
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Icon from '../components/Icon';
import { usePageMeta } from '../hooks/usePageMeta';
import './DashboardPage.css';

// the stripe checkout pages we send people to when they want to pay - one for the
// $4/mo subscription, one for the $20 lifetime. both fire checkout.session.completed,
// which is what our webhook listens for to actually make them premium.
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/7sY28tbkbdFg867cIOefC00';
const STRIPE_LIFETIME_LINK = 'https://buy.stripe.com/8x214p5ZRat45XZ5gmefC01';

export default function DashboardPage() {
  // noIndex: this is behind a login and holds account data - it should never
  // turn up in a search result
  usePageMeta({
    title: 'Your dashboard - GPTDisguise',
    description: 'Manage your GPTDisguise plan and account.',
    path: '/dashboard',
    noIndex: true
  });

  // the logged in user object (null until we've loaded it)
  const [user, setUser] = useState(null);
  // 'free' or 'premium' - what plan they're on
  const [plan, setPlan] = useState('free');
  // true while we're waiting for stripe to confirm a fresh payment
  const [checkingPlan, setCheckingPlan] = useState(false);
  // set when the poll below runs out of attempts without seeing premium land.
  // empty string means there's nothing to say.
  const [planNotice, setPlanNotice] = useState('');
  const navigate = useNavigate();

  // this whole block runs once when the dashboard first loads
  useEffect(() => {
    // refresh the session first, so someone who just upgraded sees it without
    // having to sign out and back in. the plan is baked into the token at login
    // time, so an old token would still say "free".
    // (this is a self-running function so we're allowed to use await in here)
    (async () => {
      // grab a fresh token; if it fails we just ignore it and carry on
      await supabase.auth.refreshSession().catch(() => {});
      // now ask supabase who's logged in
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // not logged in? get outta here, go to the sign in page
        navigate('/signin');
      } else {
        // logged in - remember the user and read their plan (default to free)
        setUser(user);
        setPlan(user.app_metadata?.plan || 'free');
      }
    })();

    // when stripe finishes, it sends people back with ?payment=success in the url.
    // declared out here rather than inside the if, so the cleanup at the bottom
    // can always see it - clearInterval(undefined) does nothing, so the case
    // where we never started a poll costs us nothing.
    let interval;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      // clean that ?payment=success out of the url so a refresh doesn't re-trigger this
      window.history.replaceState({}, '', '/dashboard');
      // show the "confirming your payment" spinner
      setCheckingPlan(true);
      let attempts = 0;
      // the webhook takes a sec to flip us to premium, so we keep re-checking every second
      interval = setInterval(async () => {
        attempts++;
        try {
          // refresh + re-read the user each time to see if premium landed yet
          await supabase.auth.refreshSession();
          const { data: { user } } = await supabase.auth.getUser();
          if (user?.app_metadata?.plan === 'premium') {
            // yes! payment went through, update everything and stop polling
            setPlan('premium');
            setUser(user);
            setCheckingPlan(false);
            clearInterval(interval);
            return;
          }
        } catch {
          // a dropped connection mid-poll isn't the end of the world - this tick
          // just counts as a miss and we try again in a second
        }

        if (attempts >= 10) {
          // gave it 10 tries (~10s) and still nothing. the webhook may simply be
          // slow, so say so - silently dropping back to "You're on the Free plan"
          // reads as "your payment failed" to someone who has just paid.
          clearInterval(interval);
          setCheckingPlan(false);
          setPlanNotice(
            "We haven't had confirmation from Stripe yet. If you've completed payment, " +
            'your plan will update within a few minutes - refresh this page to check.'
          );
        }
      }, 1000);
    }

    // stop the poll if they navigate away mid-payment. without this the interval
    // outlives the page and keeps calling setState on an unmounted component.
    return () => clearInterval(interval);
  }, [navigate]);

  // runs when they click an upgrade button. defaults to the monthly plan.
  const handleUpgrade = (billing = 'monthly') => {
    // can't pay if we don't know who they are yet
    if (!user) return;
    // we tack the user's id onto the stripe url so we know exactly who paid.
    // it has to be the id and not the email, because apple pay and link fill in
    // whatever wallet email they like at checkout. prefilled_email is only there
    // to save them typing. where stripe sends them afterwards is set on the
    // payment link itself in the dashboard - putting it here does nothing.
    const params = new URLSearchParams({
      client_reference_id: user.id,
      prefilled_email: user.email,
    });
    // pick which stripe page based on whether they chose lifetime or monthly
    const link = billing === 'lifetime' ? STRIPE_LIFETIME_LINK : STRIPE_PAYMENT_LINK;
    // and off to stripe we go
    window.location.href = `${link}?${params.toString()}`;
  };

  // log the user out and send them back to the homepage
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // while we're still figuring out who's logged in, draw nothing (avoids a flash)
  if (!user) return null;

  // a few little derived values we use in the ui below:
  const planType = user.app_metadata?.plan_type; // 'monthly' | 'lifetime' | undefined
  // show their full name if we have it, otherwise fall back to their email
  const displayName = user.user_metadata?.full_name || user.email;
  // just the first name, for the friendly "Hey, Bob." greeting
  const firstName = user.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ')[0]
    : null;
  // build the two-letter initials for the little avatar circle (e.g. "Bob Smith" -> "BS")
  const initials = displayName
    ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div className="dash-page">
      {/* background glow blobs, purely decorative */}
      <div className="dash-bg-orb dash-bg-orb--a" />
      <div className="dash-bg-orb dash-bg-orb--b" />

      {/* top nav bar: logo on the left, user info + sign out on the right */}
      <nav className="dash-nav">
        <a href="/" className="dash-nav-logo">
          <img src="/icon.png" alt="GPTDisguise" className="dash-nav-icon" />
          GPT<span>Disguise</span>
        </a>
        <div className="dash-nav-right">
          <div className="dash-nav-user">
            {/* the little circle with your initials */}
            <div className="dash-nav-avatar">{initials}</div>
            <span className="dash-nav-email">{user.email}</span>
          </div>
          <button className="dash-nav-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      <div className="dash-content">

        {/* the big greeting header at the top of the page */}
        <div className="dash-header">
          <h1>{firstName ? `Hey, ${firstName}.` : 'Your dashboard.'}</h1>
          {/* the subtitle changes depending on what's going on */}
          <p className="dash-header-sub">
            {checkingPlan
              ? 'Confirming your payment...'
              : plan === 'premium'
                ? "You're on Premium - all features are unlocked."
                : "You're on the Free plan. Upgrade to unlock all features."}
          </p>
        </div>

        {/* only shows right after payment while we wait for stripe to confirm */}
        {checkingPlan && (
          <div className="dash-checking-banner">
            <div className="dash-checking-spinner" />
            Confirming your payment with Stripe, this may take a few seconds...
          </div>
        )}

        {/* takes the banner's place when the poll gives up. role="status" makes a
            screen reader read it out - it appears on its own without the user
            clicking anything, so otherwise they'd never know it was there. */}
        {planNotice && (
          <div className="dash-plan-notice" role="status">{planNotice}</div>
        )}

        {/* the big plan banner - looks different for free vs premium users */}
        <div className={`dash-plan-banner ${plan === 'premium' ? 'dash-plan-banner--premium' : 'dash-plan-banner--free'}`}>
          <div className="dash-plan-banner-left">
            {/* the little badge that says "Premium" (with sparkle) or "Free plan" */}
            <div className="dash-plan-badge">
              {plan === 'premium' ? (
                <><Icon name="sparkle" size={12} strokeWidth={2} /> Premium</>
              ) : 'Free plan'}
            </div>
            {/* the blurb - either "you've got everything" or "upgrade to get..." */}
            <p>
              {plan === 'premium'
                ? 'You have full access to Premium - including Claude, Microsoft and Notion themes, keyboard shortcuts, and Gemini support (alpha). More features are on the way.'
                : 'Upgrade to Premium to unlock Claude, Microsoft and Notion themes, keyboard shortcuts, and Gemini support (alpha).'}
            </p>
          </div>
          {/* only free users (and not mid-payment) see the pricing/upgrade buttons */}
          {plan === 'free' && !checkingPlan && (
            <div className="dash-plan-banner-right">
              <div className="dash-plan-options">
                {/* the $4/month option */}
                <div className="dash-plan-option">
                  <div className="dash-price-display">
                    <span className="dash-price-amount">$4</span>
                    <span className="dash-price-period">/month</span>
                  </div>
                  <button className="dash-upgrade-btn" onClick={() => handleUpgrade('monthly')}>
                    Go monthly
                  </button>
                  <p className="dash-upgrade-note">Cancel anytime</p>
                </div>
                {/* the $20 one-time lifetime option, flagged as best value */}
                <div className="dash-plan-option dash-plan-option--lifetime">
                  <span className="dash-plan-option-badge">Best value</span>
                  <div className="dash-price-display">
                    <span className="dash-price-amount">$20</span>
                    <span className="dash-price-period">one-time</span>
                  </div>
                  <button className="dash-upgrade-btn dash-upgrade-btn--lifetime" onClick={() => handleUpgrade('lifetime')}>
                    Get Lifetime
                  </button>
                  <p className="dash-upgrade-note">Pay once, yours forever</p>
                </div>
              </div>
            </div>
          )}
        </div>


        {/* the grid of feature cards. we define them as a list of objects then loop over it */}
        <div className="dash-features-grid">
          {[
            // each feature has an icon, title, a status, and a little description.
            // some statuses depend on whether you're premium or not.
            { icon: 'disguise', title: 'Google Docs disguise', status: 'active', desc: 'Active on your extension' },
            { icon: 'command', title: 'Toggle keyboard shortcut', status: 'active', desc: 'Active on your extension' },
            { icon: 'sparkle', title: 'Claude support', status: plan === 'premium' ? 'active' : 'locked', desc: plan === 'premium' ? 'Live in extension' : 'Premium only' },
            { icon: 'sparkle', title: 'Gemini support', status: plan === 'premium' ? 'alpha' : 'locked', desc: plan === 'premium' ? 'Live in alpha' : 'Premium only' },
            { icon: 'palette', title: 'Microsoft & Notion themes', status: plan === 'premium' ? 'active' : 'locked', desc: plan === 'premium' ? 'Available in extension' : 'Premium only' },
            { icon: 'command', title: 'Keyboard shortcuts', status: plan === 'premium' ? 'active' : 'locked', desc: plan === 'premium' ? 'Available in extension' : 'Premium only' },
            { icon: 'layers', title: 'Session memory', status: 'soon', desc: 'Coming soon' },
            { icon: 'robot', title: 'More themes & disguises', status: 'soon', desc: 'Coming soon' },
          ].map(f => {
            // turn the status into a human label for the little pill (e.g. 'locked' -> 'Locked')
            const pill = { active: 'Active', alpha: 'Alpha', locked: 'Locked', soon: 'Soon' }[f.status];
            // and pick the matching icon for that pill
            const pillIcon = { active: 'check', alpha: 'flask', locked: 'lock', soon: 'clock' }[f.status];
            return (
              // one card per feature, styled by its status
              <div key={f.title} className={`dash-feat-card dash-feat-card--${f.status}`}>
                <div className="dash-feat-icon"><Icon name={f.icon} size={19} /></div>
                <div className="dash-feat-info">
                  <div className="dash-feat-title">{f.title}</div>
                  <div className="dash-feat-desc">{f.desc}</div>
                </div>
                {/* the status pill in the corner */}
                <div className={`dash-feat-pill dash-feat-pill--${f.status}`}>
                  <Icon name={pillIcon} size={12} strokeWidth={2} />
                  {pill}
                </div>
              </div>
            );
          })}
        </div>

        {/* bottom row: account details card on the left, extension card on the right */}
        <div className="dash-bottom-grid">
          {/* ACCOUNT CARD */}
          <div className="dash-card">
            <div className="dash-card-label">Account</div>
            <div className="dash-card-row">
              <div className="dash-card-avatar">{initials}</div>
              <div>
                <div className="dash-card-name">{displayName}</div>
                <div className="dash-card-email">{user.email}</div>
              </div>
            </div>
            <div className="dash-card-divider" />
            {/* shows Free / Premium / Lifetime depending on their plan */}
            <div className="dash-card-meta">
              <span>Plan</span>
              <span className={plan === 'premium' ? 'dash-meta-premium' : 'dash-meta-free'}>
                {plan === 'premium' && <Icon name="sparkle" size={13} strokeWidth={2} />}
                {plan === 'premium'
                  ? (planType === 'lifetime' ? 'Lifetime' : 'Premium')
                  : 'Free'}
              </span>
            </div>
          </div>

          {/* EXTENSION CARD - nudges them to install/sign into the chrome extension */}
          <div className="dash-card">
            <div className="dash-card-label">Extension</div>
            <h3 className="dash-card-title">GPTDisguise for Chrome</h3>
            <p className="dash-card-desc">
              Make sure the extension is installed and you're signed into the same account to sync your plan.
            </p>
            {/* links to our actual listing rather than the store's front page, so
                they don't have to go and search for us */}
            <a
              href="https://chromewebstore.google.com/detail/gptdisguise/bccghhjlejbkimijdkdfoboamcjgpefg"
              target="_blank"
              rel="noreferrer"
              className="dash-chrome-btn"
            >
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="3.5" fill="currentColor" fillOpacity="0.3"/>
                <path d="M9 5.5h7.26A8 8 0 1 0 9 17V5.5z" fill="none" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
              Open Chrome Web Store
            </a>
          </div>
        </div>

        {/* only premium folks see this - how to cancel, since there's no self-serve button yet */}
        {plan === 'premium' && (
          <p className="dash-cancel-note">
            If you would like to cancel your Premium, please email{' '}
            <a href="mailto:cloakstudios@outlook.com">cloakstudios@outlook.com</a>.
          </p>
        )}

      </div>
    </div>
  );
}
